/**
 * Wallet transactions hooks — READ ONLY. No mutation hooks exist (the backend
 * exposes no transaction create/update/delete; balances change only via the
 * official wallet top-up). Reads fall back to mock data.
 */

import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import { walletTopups as mockTopups } from "@/mocks/seed";
import type { WalletTopup } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { walletTransactionsApi, type WalletTransactionsListParams } from "./wallet-transactions.api";
import { toWalletTopup, toWalletTransactionView, type WalletTransactionView } from "./wallet-transactions.adapter";

const RECENT_TOPUPS_PARAMS = { type: "TOPUP", limit: 10, sortBy: "occurredAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseWalletTransactionsResult {
  topups: WalletTopup[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
}

/** Recent top-ups across all wallets (for the WalletsPage side panel). */
export function useWalletTransactions(): UseWalletTransactionsResult {
  const authed = useAuthStore((s) => s.status === "authenticated");

  const query = useQuery({
    queryKey: queryKeys.walletTransactions(RECENT_TOPUPS_PARAMS),
    queryFn: () => walletTransactionsApi.list(RECENT_TOPUPS_PARAMS),
    enabled: authed,
  });

  if (!authed) return { topups: mockTopups, isLoading: false, isError: false, source: "mock", refetch: query.refetch };
  if (query.data) return { topups: query.data.data.map(toWalletTopup), isLoading: false, isError: false, source: "api", refetch: query.refetch };
  if (query.isError) return { topups: mockTopups, isLoading: false, isError: true, source: "mock", refetch: query.refetch };
  return { topups: [], isLoading: query.isLoading, isError: false, source: "api", refetch: query.refetch };
}

export interface UseWalletTransactionsByWalletResult {
  transactions: WalletTransactionView[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
}

/** Full transaction history for a single wallet (read-only). */
export function useWalletTransactionsByWallet(
  walletId: string | undefined,
  params: WalletTransactionsListParams = {},
): UseWalletTransactionsByWalletResult {
  const authed = useAuthStore((s) => s.status === "authenticated");

  const query = useQuery({
    queryKey: ["wallet-transactions", "by-wallet", walletId, params],
    queryFn: () => walletTransactionsApi.listByWallet(walletId as string, params),
    enabled: authed && !!walletId,
  });

  if (query.data) return { transactions: query.data.data.map(toWalletTransactionView), isLoading: false, isError: false, source: "api" };
  if (query.isError) return { transactions: [], isLoading: false, isError: true, source: "mock" };
  return { transactions: [], isLoading: authed && !!walletId && query.isLoading, isError: false, source: authed ? "api" : "mock" };
}
